import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { reportText } from "./reportPresentation";
import { SUPPORTED_MOBILITY_SECTIONS } from "../../../../utlis/mobilityReportSections";
import { reportPhotoSource } from "../../../../utlis/reportMedia";
import "./RenderReportDocument.scss";

// This module only presents server results. Grade, trend, freshness, grouping,
// recommendation and FIM calculations deliberately have no implementation here.
const list = (value) =>
  Array.isArray(value)
    ? value
    : value && typeof value === "object"
      ? Object.values(value)
      : [];
const text = (value) =>
  typeof value === "string" || typeof value === "number" ? String(value) : "";
const label = (value) =>
  text(value?.label ?? value?.display_name ?? value?.name ?? value?.title);
const allowed = (value) => value === true || value?.allowed === true;
const token = (value) =>
  text(value)
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "");
export const safeReportUrl = (value) => {
  if (typeof value !== "string" || !value.trim()) return "";
  try {
    const url = new URL(value, window.location.origin);
    return ["http:", "https:"].includes(url.protocol) &&
      !url.username &&
      !url.password
      ? url.href
      : "";
  } catch {
    return "";
  }
};

const sectionTypes = new Set(SUPPORTED_MOBILITY_SECTIONS);
const has = (value, key) =>
  Object.prototype.hasOwnProperty.call(value || {}, key);
// An explicitly null effective/display result is authoritative, not a request
// to substitute its raw canonical value or a different clinical score.
const effectiveScore = (value) =>
  has(value, "effective_score") ? value.effective_score : value.score;
const scoreDisplay = (value) =>
  text(
    has(value, "effective_score_display")
      ? value.effective_score_display
      : has(value, "display_score")
        ? value.display_score
        : effectiveScore(value),
  );
const valueDisplay = (value) => text(value?.display_value);
export const validateReportSections = (report) => {
  const sections = report.presentation?.sections;
  if (
    !Array.isArray(sections) ||
    !sections.length ||
    sections.some(
      (section) =>
        section?.visible !== false &&
        !sectionTypes.has(typeof section === "string" ? section : section.type),
    )
  ) {
    const error = new Error("Unsupported report presentation.");
    error.code = "UnsupportedMobilityReport";
    throw error;
  }
  return sections;
};

export const printableMetricIds = (report) => {
  const referenced = [];
  const add = (source) => {
    if (!source || source.visible === false) return;
    referenced.push(...(source.metric_ids || source.driving_metric_ids || []));
    list(source.groups).forEach(add);
  };
  for (const entry of report.presentation.sections) {
    const section = typeof entry === "string" ? { type: entry } : entry;
    if (section.visible === false) continue;
    if (section.type === "metrics")
      referenced.push(
        ...(section.metric_ids ??
          report.presentation.metric_order ??
          Object.keys(report.metrics)),
      );
    if (section.type === "domains")
      list(report.domains)
        .filter(
          (domain) =>
            !section.domain_ids ||
            section.domain_ids.includes(domain.id ?? domain.key),
        )
        .forEach(add);
    if (section.type === "groups") list(report.groups).forEach(add);
    if (section.type === "longevity")
      list(report.longevity?.groups).forEach(add);
    if (section.type === "fim") list(report.fim?.items).forEach(add);
  }
  return [
    ...new Set(report.presentation.print_metric_ids ?? referenced),
  ].filter(
    (id) =>
      report.metrics[id]?.visible !== false &&
      report.metrics[id]?.print_visible !== false,
  );
};

function CopyBlock({ value }) {
  if (value === null || value === undefined || value === false) return null;
  if (typeof value === "string" || typeof value === "number")
    return <p>{value}</p>;
  if (Array.isArray(value))
    return (
      <>
        {value.map((entry, index) => (
          <CopyBlock key={index} value={entry} />
        ))}
      </>
    );
  if (value.type === "list" || Array.isArray(value.items))
    return (
      <div>
        {value.title && <h4>{text(value.title)}</h4>}
        <ul>
          {list(value.items).map((entry, index) => (
            <li key={index}>
              <CopyBlock value={entry} />
            </li>
          ))}
        </ul>
      </div>
    );
  const body =
    value.text ??
    value.body ??
    value.description ??
    value.explanation ??
    value.summary_text ??
    value.note ??
    value.meaning ??
    value.rationale ??
    value.label;
  return (
    <>
      {value.title && <h4>{text(value.title)}</h4>}
      {value.label && body !== value.label && (
        <strong>{text(value.label)}</strong>
      )}
      {body != null && <CopyBlock value={body} />}
      {value.sections && <CopyBlock value={value.sections} />}
    </>
  );
}

function Status({ value, fallback }) {
  if (!value)
    return fallback ? (
      <span className="mr-status mr-status-neutral">{fallback}</span>
    ) : null;
  const state = typeof value.status === "object" ? value.status : value;
  const name = text(
    state.status_label ?? state.grade_label ?? state.level_label ?? state.label,
  );
  const code = state.token ?? state.grade ?? state.status ?? state.level;
  if (
    (!code &&
      !state.status_label &&
      !state.grade_label &&
      !state.level_label) ||
    typeof code === "object"
  )
    return null;
  return name ? (
    <span className={`mr-status mr-status-${token(code) || "neutral"}`}>
      {name}
    </span>
  ) : null;
}
function Trend({ value }) {
  const direction = typeof value === "string" ? value : value?.direction;
  const glyph = { up: "↑", down: "↓", flat: "→" }[direction];
  if (!glyph) return null;
  return (
    <span
      className={`mr-trend mr-trend-${direction}`}
      aria-label={text(value?.label) || undefined}
    >
      {glyph}
      {value?.label && <span className="mr-sr-only">{value.label}</span>}
    </span>
  );
}
function Freshness({ value, small = false }) {
  if (!value?.outdated) return null;
  return (
    <span className={`mr-outdated${small ? " mr-outdated-small" : ""}`}>
      {text(value.label ?? value.badge_label)}
    </span>
  );
}

export function ReportDialog({ title, children, onClose, closeLabel }) {
  const ref = useRef(null);
  const id = useId();
  useEffect(() => {
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    ref.current?.querySelector("button")?.focus();
    const key = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
      if (event.key === "Tab") {
        const nodes = Array.from(
          ref.current?.querySelectorAll(
            'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]',
          ) || [],
        );
        const first = nodes[0],
          last = nodes[nodes.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("keydown", key);
      document.body.style.overflow = overflow;
      if (previous?.isConnected) previous.focus();
    };
  }, [onClose]);
  return (
    <div className="mr-dialog-backdrop" onClick={onClose}>
      <section
        ref={ref}
        className="mr-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        onClick={(event) => event.stopPropagation()}
      >
        <header>
          <h2 id={id}>{title}</h2>
          <button type="button" onClick={onClose}>
            {closeLabel}
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

function Media({ src, name, className, placeholder }) {
  const [failedUrl, setFailedUrl] = useState(null);
  const url = safeReportUrl(src);
  if (!url || failedUrl === url)
    return (
      <span
        className={`${className || ""} mr-media-placeholder`}
        aria-hidden="true"
      >
        {placeholder ?? "◌"}
      </span>
    );
  return (
    <img
      className={className}
      src={url}
      alt={name || ""}
      onError={() => setFailedUrl(url)}
    />
  );
}

function Series({ chart, t, compact = false }) {
  const points = list(chart.points ?? chart.history);
  const [end, setEnd] = useState(points.length);
  const [wide, setWide] = useState(() => window.innerWidth >= 768);
  const startX = useRef(null);
  useEffect(() => {
    const resize = () => setWide(window.innerWidth >= 768);
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);
  useEffect(() => setEnd(points.length), [chart, points.length]);
  const count = wide ? 6 : 4;
  const last = Math.min(end, points.length);
  const shown = points.slice(Math.max(0, last - count), last);
  const numeric = points
    .map((p) => p?.value)
    .filter((v) => typeof v === "number" && Number.isFinite(v));
  // These extents position the supplied observations; they never classify data.
  const minimum =
    typeof chart.y_range?.min === "number"
      ? chart.y_range.min
      : Math.min(...numeric, 0);
  const maximum =
    typeof chart.y_range?.max === "number"
      ? chart.y_range.max
      : Math.max(...numeric, 1);
  const dates = points.map((p) => Date.parse(p?.date)).filter(Number.isFinite);
  const firstDate = Math.min(...dates),
    lastDate = Math.max(...dates);
  const draw = (data) => {
    const x = (p, i) =>
      Number.isFinite(Date.parse(p.date)) && firstDate !== lastDate
        ? 28 + ((Date.parse(p.date) - firstDate) / (lastDate - firstDate)) * 584
        : 28 + (i / Math.max(data.length - 1, 1)) * 584;
    const visibleDates = data
      .map((p) => Date.parse(p.date))
      .filter(Number.isFinite);
    const dMin = Math.min(...visibleDates),
      dMax = Math.max(...visibleDates);
    const localX = (p, i) =>
      dMax > dMin && Number.isFinite(Date.parse(p.date))
        ? 28 + ((Date.parse(p.date) - dMin) / (dMax - dMin)) * 584
        : x(p, i);
    const y = (value) =>
      142 -
      ((value - minimum) / Math.max(maximum - minimum, Number.EPSILON)) * 112;
    let segments = [[]];
    data.forEach((p, i) => {
      if (typeof p?.value !== "number" || !Number.isFinite(p.value))
        segments.push([]);
      else segments[segments.length - 1].push([localX(p, i), y(p.value)]);
    });
    return (
      <svg
        viewBox="0 0 640 184"
        role="img"
        aria-label={label(chart) || t("history")}
      >
        {list(chart.bands).map((band, index) =>
          typeof band.min === "number" && typeof band.max === "number" ? (
            <rect
              key={index}
              x="28"
              width="584"
              y={y(band.max)}
              height={Math.max(0, y(band.min) - y(band.max))}
              className={`mr-band mr-band-${token(band.status ?? band.grade)}`}
            >
              <title>{label(band)}</title>
            </rect>
          ) : null,
        )}
        <path d="M28 20V142H612" className="mr-axis" />
        {segments
          .filter((segment) => segment.length > 1)
          .map((segment, i) => (
            <polyline
              key={i}
              points={segment.map((p) => p.join(",")).join(" ")}
              className="mr-line"
            />
          ))}
        {data.map((p, i) => (
          <g key={`${p.date}-${i}`}>
            {typeof p.value === "number" && Number.isFinite(p.value) && (
              <circle
                cx={localX(p, i)}
                cy={y(p.value)}
                r="4"
                className={`mr-dot mr-dot-${token(p.grade ?? p.status)}`}
              >
                <title>{`${text(p.date_label ?? p.date)}: ${valueDisplay(p) || "—"} ${text(chart.display_unit ?? chart.unit)}`}</title>
              </circle>
            )}
            {(i % Math.max(1, Math.ceil(data.length / 6)) === 0 ||
              i === data.length - 1) && (
              <text
                x={localX(p, i)}
                y="169"
                textAnchor="middle"
                className="mr-chart-label"
              >
                {text(p.date_label ?? p.date)}
              </text>
            )}
          </g>
        ))}
      </svg>
    );
  };
  if (!points.length) return <p className="mr-muted">{t("no_history")}</p>;
  const move = (delta) =>
    setEnd((value) =>
      Math.min(
        points.length,
        Math.max(Math.min(count, points.length), value + delta),
      ),
    );
  return (
    <div className={`mr-chart${compact ? " mr-chart-compact" : ""}`}>
      {label(chart) && (
        <div className="mr-chart-heading">
          <strong>{label(chart)}</strong>
          <Trend value={chart.trend ?? chart.trend_direction} />
        </div>
      )}
      <div
        className="mr-chart-screen"
        onPointerDown={(event) => {
          startX.current = event.clientX;
        }}
        onPointerUp={(event) => {
          if (
            startX.current != null &&
            Math.abs(event.clientX - startX.current) > 40
          )
            move(event.clientX > startX.current ? -1 : 1);
          startX.current = null;
        }}
      >
        {draw(shown)}
      </div>
      <div className="mr-chart-print">{draw(points)}</div>
      {points.length > count && (
        <nav className="mr-chart-controls" aria-label={t("history")}>
          <button
            type="button"
            disabled={last <= count}
            onClick={() => move(-1)}
          >
            {t("previous")}
          </button>
          <span>
            {Math.max(1, last - count + 1)}–{last} / {points.length}
          </span>
          <button
            type="button"
            disabled={last >= points.length}
            onClick={() => move(1)}
          >
            {t("next")}
          </button>
        </nav>
      )}
      <details className="mr-chart-data">
        <summary>{t("details")}</summary>
        <table>
          <thead>
            <tr>
              <th>{t("date")}</th>
              <th>{t("value")}</th>
              <th>{t("status")}</th>
              <th>{t("source")}</th>
            </tr>
          </thead>
          <tbody>
            {points.map((p, index) => (
              <tr key={index}>
                <td>{text(p.date_label ?? p.date)}</td>
                <td>
                  {valueDisplay(p) || "—"}{" "}
                  {text(chart.display_unit ?? chart.unit)}
                </td>
                <td>
                  {text(p.grade_label ?? p.status_label ?? p.label) || "—"}
                </td>
                <td>
                  {text(
                    p.source?.test_label ?? p.source?.test_type ?? p.test_label,
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}

function TierHistory({ values }) {
  if (!Array.isArray(values) || !values.length) return null;
  return (
    <div className="mr-tier-history">
      {values.map((point, index) => (
        <span
          key={index}
          className={`mr-tier mr-tier-${token(point.tier ?? point.status)}`}
          title={`${text(point.date_label ?? point.date)} ${text(point.label ?? point.status_label)}`}
        >
          <span>{text(point.date_label ?? point.date)}</span>
        </span>
      ))}
    </div>
  );
}

function MissingMeasures({ source, metrics, t }) {
  const missing = list(source.missing_core);
  const tests = list(source.add_tests);
  if (!missing.length && !tests.length) return null;
  return (
    <div className="mr-missing-measures">
      <h4>{source.missing_core_label || t("missing_measures")}</h4>
      {missing.length > 0 && (
        <ul>
          {missing.map((entry, index) => (
            <li key={index}>
              {typeof entry === "string" ? label(metrics[entry]) : label(entry)}
              {typeof entry === "object" && (
                <CopyBlock value={entry.reason ?? entry.note} />
              )}
            </li>
          ))}
        </ul>
      )}
      {tests.length > 0 && (
        <ul>
          {tests.map((entry, index) => (
            <li key={index}>
              {typeof entry === "string" ? entry : label(entry)}
              {typeof entry === "object" && <CopyBlock value={entry.note} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MetricInformation({ metric, t }) {
  return (
    <div className="mr-metric-detail">
      <div className="mr-section-heading">
        <strong className="mr-detail-value">
          {valueDisplay(metric) || "—"}{" "}
          <small>{text(metric.display_unit ?? metric.unit)}</small>
        </strong>
        <Status value={metric} />
        <Freshness value={metric.freshness} />
      </div>
      <CopyBlock value={metric.score_note ?? metric.score_reason} />
      <CopyBlock value={metric.rationale ?? metric.explanation} />
      <dl className="mr-facts">
        <div>
          <dt>{t("date")}</dt>
          <dd>
            {text(metric.measurement_date_label ?? metric.measurement_date)}
          </dd>
        </div>
        <div>
          <dt>{t("source")}</dt>
          <dd>{text(metric.source?.test_label ?? metric.source?.test_type)}</dd>
        </div>
        <div>
          <dt>{t("confidence")}</dt>
          <dd>
            {text(
              metric.source?.confidence_label ??
                metric.source?.confidence ??
                metric.confidence,
            )}
          </dd>
        </div>
      </dl>
      <Series
        chart={
          metric.chart ?? {
            points: metric.history,
            label: label(metric),
            display_unit: metric.display_unit,
            bands: metric.display_ranges?.bands,
          }
        }
        t={t}
      />
      <h3>{t("how_calculated")}</h3>
      <CopyBlock value={metric.how_calculated ?? metric.explanation_sections} />
      <CopyBlock value={metric.thresholds?.display_label} />
      {list(metric.thresholds?.rows).map((row, index) => (
        <p key={index}>
          <strong>{label(row)}: </strong>
          {valueDisplay(row)} {text(row.display_unit)}
        </p>
      ))}
      <CopyBlock value={metric.freshness?.notice ?? metric.freshness?.note} />
    </div>
  );
}

const targetFor = (report, source) => {
  const id = source?.assessment_id ?? report.assessment?.assessment_id;
  return list(report.edit_targets).find(
    (target) => String(target.assessment_id) === String(id),
  );
};
const editIdentity = (target) => ({
  assessment_id: target.assessment_id,
  expected_assessment_revision:
    target.assessment_revision ??
    target.revision ??
    target.expected_assessment_revision,
});

function Remark({ report, kind, value, readOnly, onEdit, busy, t }) {
  const [editing, setEditing] = useState(false),
    [draft, setDraft] = useState(""),
    [error, setError] = useState(false);
  const source =
    typeof value === "object" && value ? value : { text: text(value) };
  const target = targetFor(report, source);
  const fields = target?.editable_fields ?? target?.fields ?? [];
  const permitted =
    allowed(source.editable) &&
    target &&
    (fields.includes(`remarks.${kind}`) ||
      fields.includes("remarks") ||
      allowed(target.can_edit_remarks));
  const canEdit =
    !readOnly &&
    onEdit &&
    allowed(report.capabilities.can_edit_remarks) &&
    permitted;
  const save = async (event) => {
    event.preventDefault();
    setError(false);
    try {
      await onEdit({ ...editIdentity(target), remarks: { [kind]: draft } });
      setEditing(false);
    } catch {
      setError(true);
    }
  };
  const max =
    source.max_length ??
    report.remarks?.limits?.[kind] ??
    (kind === "staff" ? 200 : 120);
  return (
    <div className="mr-remark">
      <div className="mr-section-heading">
        <h3>
          {label(source) ||
            t(kind === "staff" ? "staff_remarks" : "person_remarks")}
        </h3>
        {canEdit && !editing && (
          <button
            type="button"
            onClick={() => {
              setDraft(text(source.text ?? source.value));
              setEditing(true);
              setError(false);
            }}
          >
            {t("edit")}
          </button>
        )}
      </div>
      {editing && (
        <p className="mr-saved-remark-print notranslate" translate="no">
          {text(source.display_text ?? source.text ?? source.value) || "—"}
        </p>
      )}
      {editing ? (
        <form onSubmit={save}>
          <textarea
            aria-label={
              label(source) ||
              t(kind === "staff" ? "staff_remarks" : "person_remarks")
            }
            maxLength={max}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            disabled={busy}
          />
          <span className="mr-muted">
            {draft.length}/{max}
          </span>
          {error && <p role="alert">{t("saveFailed")}</p>}
          <div className="mr-form-actions">
            <button type="submit" disabled={busy}>
              {t("save")}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setEditing(false)}
            >
              {t("cancel")}
            </button>
          </div>
        </form>
      ) : (
        <p className="notranslate" translate="no">
          {text(source.display_text ?? source.text ?? source.value) || "—"}
        </p>
      )}
    </div>
  );
}

function FimEditor({ item, source, report, readOnly, onEdit, busy, t }) {
  const [editing, setEditing] = useState(false),
    [draft, setDraft] = useState(""),
    [error, setError] = useState(false);
  const target = targetFor(report, source);
  const ids = target?.editable_item_ids ?? target?.item_ids ?? [];
  const canEdit =
    !readOnly &&
    onEdit &&
    target &&
    allowed(source.editable ?? item.editable) &&
    allowed(report.capabilities.can_edit_fim) &&
    (ids.includes(item.item_id) || allowed(target.can_edit_fim));
  if (!canEdit) return null;
  const save = async (score) => {
    setError(false);
    try {
      await onEdit({
        ...editIdentity(target),
        fim_overrides: [{ item_id: item.item_id, fim_override: score }],
      });
      setEditing(false);
    } catch {
      setError(true);
    }
  };
  return editing ? (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (/^[1-7]$/.test(draft)) save(Number(draft));
      }}
    >
      <label>
        {t("clinician_override")}
        <input
          aria-label={`${t("clinician_override")} ${label(item)} ${text(source.date_label)}`}
          type="number"
          min="1"
          max="7"
          step="1"
          required
          value={draft}
          disabled={busy}
          onChange={(event) => setDraft(event.target.value)}
        />
      </label>
      {error && <p role="alert">{t("saveFailed")}</p>}
      <div className="mr-form-actions">
        <button type="submit" disabled={busy || !/^[1-7]$/.test(draft)}>
          {t("save")}
        </button>
        <button type="button" disabled={busy} onClick={() => save(null)}>
          {t("clear_override")}
        </button>
        <button type="button" disabled={busy} onClick={() => setEditing(false)}>
          {t("cancel")}
        </button>
      </div>
    </form>
  ) : (
    <button
      type="button"
      aria-label={`${t("edit")} ${label(item)} ${text(source.date_label)}`}
      disabled={busy}
      onClick={() => {
        setDraft(text(source.clinician_override ?? effectiveScore(source)));
        setEditing(true);
      }}
    >
      {t("edit")}
    </button>
  );
}
function FimItem({ item, report, readOnly, onEdit, busy, t, open }) {
  const history = list(item.score_history ?? item.history);
  const points = history.map((point) => ({
    ...point,
    value: effectiveScore(point),
    display_value: scoreDisplay(point),
    status_label: point.level_label ?? point.status_label,
  }));
  return (
    <article className="mr-fim-item">
      <div className="mr-section-heading">
        <h3>{label(item)}</h3>
        <div>
          <Status value={item} />
          <strong className="mr-score">{scoreDisplay(item) || "—"}</strong>
          <Trend value={item.trend ?? item.trend_direction} />
        </div>
      </div>
      <CopyBlock value={item.observation_label ?? item.computability_label} />
      <CopyBlock value={item.level_label ?? item.level?.label} />
      <CopyBlock value={item.explanation ?? item.reason} />
      <dl className="mr-facts">
        <div>
          <dt>{t("computed_score")}</dt>
          <dd>
            {text(item.computed_score_display ?? item.computed_score) || "—"}
          </dd>
        </div>
        {item.clinician_override != null && (
          <div>
            <dt>{t("clinician_override")}</dt>
            <dd>{text(item.clinician_override)}</dd>
          </div>
        )}
      </dl>
      {item.driving_metric_ids && (
        <div className="mr-driver-list">
          {item.driving_metric_ids.map((id) => (
            <button type="button" key={id} onClick={() => open(id)}>
              {label(report.metrics[id])}
            </button>
          ))}
        </div>
      )}
      <Series
        chart={{ label: label(item), points, y_range: item.y_range }}
        t={t}
      />
      <FimEditor
        item={item}
        source={item}
        report={report}
        readOnly={readOnly}
        onEdit={onEdit}
        busy={busy}
        t={t}
      />
      {history.length > 0 && (
        <details className="mr-fim-history">
          <summary>{t("history")}</summary>
          <table>
            <thead>
              <tr>
                <th>{t("date")}</th>
                <th>{t("computed_score")}</th>
                <th>{t("effective_score")}</th>
                <th>{t("source")}</th>
              </tr>
            </thead>
            <tbody>
              {history.map((point, index) => (
                <tr key={`${point.assessment_id ?? point.date}-${index}`}>
                  <td>{text(point.date_label ?? point.date)}</td>
                  <td>
                    {text(
                      point.computed_score_display ?? point.computed_score,
                    ) || "—"}
                  </td>
                  <td>{scoreDisplay(point) || "—"}</td>
                  <td>
                    {text(point.source_label ?? point.source)}
                    <FimEditor
                      item={item}
                      source={point}
                      report={report}
                      readOnly={readOnly}
                      onEdit={onEdit}
                      busy={busy}
                      t={t}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </article>
  );
}

function Exercise({ exercise }) {
  if (!exercise) return null;
  return (
    <article className="mr-exercise">
      <Media
        src={exercise.image_url ?? exercise.aws_url ?? exercise.media?.url}
        name={label(exercise)}
        className="mr-exercise-image"
      />
      <div>
        <h4>{label(exercise)}</h4>
        <CopyBlock value={exercise.instructions ?? exercise.how_to} />
        <CopyBlock value={exercise.note} />
        <CopyBlock value={exercise.frequency_duration ?? exercise.frequency} />
        <CopyBlock value={exercise.cautions ?? exercise.caution} />
        <CopyBlock value={exercise.notes} />
      </div>
    </article>
  );
}

function RenderReportDocument({
  report,
  readOnly = false,
  onEdit,
  onActivityChange,
  busy = false,
}) {
  const sections = validateReportSections(report);
  const locale = report.context.content_locale;
  const t = (key) => reportText(locale, key);
  const root = useRef(null);
  const [dialog, setDialog] = useState(null);
  const closeDialog = useCallback(() => setDialog(null), []);
  const detailState = useRef([]);
  useEffect(() => {
    const before = () => {
      detailState.current = Array.from(
        root.current?.querySelectorAll("details") || [],
      ).map((node) => [node, node.open]);
      detailState.current.forEach(([node]) => {
        node.open = true;
      });
    };
    const after = () =>
      detailState.current.forEach(([node, wasOpen]) => {
        node.open = wasOpen;
      });
    window.addEventListener("beforeprint", before);
    window.addEventListener("afterprint", after);
    return () => {
      window.removeEventListener("beforeprint", before);
      window.removeEventListener("afterprint", after);
    };
  }, []);
  useEffect(
    () => setDialog(null),
    [report.report_id, report.revision, report.context.activity_id],
  );
  const metrics = report.metrics;
  const openMetric = (id) => {
    if (metrics[id])
      setDialog({ metric: metrics[id], title: label(metrics[id]) });
  };
  const idsFor = (source) =>
    source.metric_ids ??
    (Array.isArray(source.metrics)
      ? source.metrics.map((value) =>
          typeof value === "string" ? value : value.metric_api ?? value.id,
        )
      : []);
  const metricRows = (ids) => (
    <div className="mr-metrics">
      {ids.map((id) => {
        const metric = metrics[id];
        if (!metric || metric.visible === false) return null;
        return (
          <button
            type="button"
            className="mr-metric-row"
            key={id}
            onClick={() => openMetric(id)}
          >
            <span>
              <strong>{label(metric)}</strong>
              {metric.group_label && <small>{metric.group_label}</small>}
            </span>
            <span className="mr-metric-value">
              {valueDisplay(metric) || "—"}
              <small>{text(metric.display_unit ?? metric.unit)}</small>
            </span>
            <span className="mr-metric-status">
              <Status value={metric} />
              <Trend value={metric.trend ?? metric.trend_direction} />
              {metric.freshness?.outdated && (
                <small>
                  {text(
                    metric.freshness.stale_date_label ??
                      metric.measurement_date_label ??
                      metric.measurement_date,
                  )}
                </small>
              )}
            </span>
            <span aria-hidden="true" className="mr-chevron">
              ›
            </span>
          </button>
        );
      })}
    </div>
  );
  const domainList = (values) =>
    values
      .filter((domain) => domain.visible !== false)
      .map((domain, index) => (
        <details
          className="mr-domain"
          key={domain.id ?? domain.key ?? index}
          open={domain.expanded === true}
        >
          <summary>
            <div>
              <h3>{label(domain)}</h3>
              <CopyBlock value={domain.purpose ?? domain.subtitle} />
            </div>
            <span>
              <Status value={domain} />
              <Freshness
                value={
                  domain.freshness ?? {
                    outdated: domain.outdated,
                    label: report.freshness.label,
                  }
                }
                small
              />
              <Trend value={domain.trend ?? domain.trend_direction} />
            </span>
          </summary>
          <CopyBlock value={domain.explanation ?? domain.summary_text} />
          <CopyBlock value={domain.status_note ?? domain.note} />
          <MissingMeasures source={domain} metrics={metrics} t={t} />
          <TierHistory values={domain.tier_history} />
          {list(domain.groups).length
            ? list(domain.groups)
                .filter((group) => group.visible !== false)
                .map((group, i) => (
                  <div key={group.id ?? i}>
                    <h4>{label(group)}</h4>
                    {metricRows(idsFor(group))}
                  </div>
                ))
            : metricRows(idsFor(domain))}
          {domain.how_assessed && (
            <button
              type="button"
              onClick={() =>
                setDialog({
                  title: t("how_assessed"),
                  content: domain.how_assessed,
                })
              }
            >
              {t("how_assessed")}
            </button>
          )}
        </details>
      ));
  const activity = report.longevity;
  const fim = report.fim;
  const sectionContent = (type, section) => {
    switch (type) {
      case "headline":
        return (
          <div className="mr-headline">
            <div className="mr-section-heading">
              <Status value={report.headline} />
              <Trend
                value={
                  report.headline?.trend ?? report.headline?.trend_direction
                }
              />
            </div>
            <CopyBlock
              value={report.headline?.summary ?? report.headline?.summary_text}
            />
            <CopyBlock value={report.headline?.explanation} />
            <CopyBlock value={report.context.completeness_note} />
          </div>
        );
      case "summary_table":
        return (
          <table className="mr-summary-table">
            <thead>
              <tr>
                <th>{report.summary_table?.labels?.domain ?? t("metric")}</th>
                <th>{report.summary_table?.labels?.status ?? t("status")}</th>
                <th>
                  {report.summary_table?.labels?.why_flagged ??
                    t("explanation")}
                </th>
              </tr>
            </thead>
            <tbody>
              {list(report.summary_table?.rows)
                .filter((domain) => domain.visible !== false)
                .map((domain) => (
                  <tr key={domain.id}>
                    <th scope="row">{label(domain)}</th>
                    <td>
                      <Status value={domain} />
                      <Trend value={domain.summary_trend ?? domain.trend} />
                      <Freshness
                        value={
                          domain.freshness ?? {
                            outdated: domain.outdated,
                            label: report.freshness.label,
                          }
                        }
                        small
                      />
                    </td>
                    <td>
                      <CopyBlock
                        value={
                          domain.why_flagged ??
                          domain.explanation ??
                          domain.summary
                        }
                      />
                      <CopyBlock value={domain.note} />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        );
      case "domains":
        return domainList(
          section.domain_ids
            ? section.domain_ids
                .map((id) =>
                  list(report.domains).find((d) => (d.id ?? d.key) === id),
                )
                .filter(Boolean)
            : list(report.domains),
        );
      case "metrics":
        return metricRows(
          section.metric_ids ??
            report.presentation.metric_order ??
            Object.keys(metrics),
        );
      case "groups":
        return domainList(list(report.groups));
      case "charts":
        return list(report.charts)
          .filter(
            (chart) =>
              !section.chart_ids || section.chart_ids.includes(chart.id),
          )
          .map((chart, i) => (
            <article className="mr-chart-card" key={chart.id ?? i}>
              <Series chart={chart} t={t} />
              <CopyBlock value={chart.explanation} />
            </article>
          ));
      case "recommendations":
        return list(
          report.recommendations?.categories ?? report.recommendations,
        )
          .filter((category) => category.visible !== false)
          .map((category, i) => (
            <article className="mr-recommendation" key={category.id ?? i}>
              <h3>{label(category)}</h3>
              <CopyBlock value={category.explanation} />
              <CopyBlock value={category.cautions} />
              <CopyBlock value={category.modifications} />
              <CopyBlock value={category.exclusions} />
              {category.unavailable ? (
                <CopyBlock value={category.reason} />
              ) : (
                <>
                  <Exercise exercise={category.primary} />
                  {!!list(category.alternatives).length && (
                    <details>
                      <summary>
                        {t("other_options")} ({category.alternatives.length})
                      </summary>
                      {category.alternatives.map((exercise, index) => (
                        <Exercise
                          key={exercise.id ?? index}
                          exercise={exercise}
                        />
                      ))}
                    </details>
                  )}
                </>
              )}
            </article>
          ));
      case "remarks":
        return (
          <div className="mr-remarks">
            {["staff", "person"]
              .filter((kind) => report.remarks?.[kind] !== undefined)
              .map((kind) => (
                <Remark
                  key={kind}
                  report={report}
                  kind={kind}
                  value={report.remarks[kind]}
                  readOnly={readOnly}
                  onEdit={onEdit}
                  busy={busy}
                  t={t}
                />
              ))}
          </div>
        );
      case "fim":
        return (
          fim && (
            <>
              <CopyBlock value={fim.summary ?? fim.summary_text} />
              <div className="mr-fim-total">
                <h3>{label(fim.total) || t("effective_score")}</h3>
                <strong>
                  {text(fim.total?.display_value) ||
                    scoreDisplay(fim.total || {}) ||
                    "—"}
                </strong>
                <Status value={fim.total} />
                <CopyBlock value={fim.total?.level_label} />
                <CopyBlock value={fim.total?.coverage_label} />
                <CopyBlock value={fim.gain_label ?? fim.total?.gain_label} />
                <CopyBlock
                  value={fim.recovery_label ?? fim.total?.recovery_label}
                />
              </div>
              {list(fim.categories).map((category, index) => (
                <div key={index} className="mr-fim-category">
                  <h3>{label(category)}</h3>
                  <strong>
                    {text(category.display_value) ||
                      scoreDisplay(category) ||
                      "—"}
                  </strong>
                  <CopyBlock value={category.coverage_label} />
                  <CopyBlock value={category.level_label} />
                </div>
              ))}
              {list(fim.charts).map((chart, i) => (
                <Series key={i} chart={chart} t={t} />
              ))}
              {list(fim.items).map((item, i) => (
                <FimItem
                  key={`${item.item_id ?? i}:${item.assessment_id ?? ""}`}
                  item={item}
                  report={report}
                  readOnly={readOnly}
                  onEdit={onEdit}
                  busy={busy}
                  t={t}
                  open={openMetric}
                />
              ))}
            </>
          )
        );
      case "longevity":
        return (
          activity && (
            <>
              <div className="mr-section-heading">
                <h3>
                  {label(activity.activity ?? activity.selected_activity)}
                </h3>
                <Status value={activity.readiness} />
                <Trend value={activity.readiness?.trend} />
              </div>
              <CopyBlock
                value={
                  activity.readiness?.explanation ??
                  activity.readiness?.summary_text
                }
              />
              <CopyBlock value={activity.prerequisite_note} />
              <CopyBlock
                value={
                  activity.core_completeness_note ?? activity.completeness_note
                }
              />
              <CopyBlock value={activity.missing_core_note} />
              <MissingMeasures source={activity} metrics={metrics} t={t} />
              {activity.safety_overlay && (
                <aside className="mr-safety-overlay">
                  <Status value={activity.safety_overlay} />
                  <CopyBlock value={activity.safety_overlay.explanation} />
                </aside>
              )}
              {domainList(list(activity.groups))}
              {list(activity.scenarios).length > 0 && (
                <details>
                  <summary>{t("explanation")}</summary>
                  <CopyBlock value={activity.scenarios} />
                </details>
              )}
              {list(activity.charts).map((chart, i) => (
                <Series key={i} chart={chart} t={t} />
              ))}
              {activity.readiness?.history && (
                <TierHistory values={activity.readiness.history} />
              )}
            </>
          )
        );
      case "explanations":
        return list(report.explanations).map((explanation, index) => (
          <details className="mr-explanation" key={explanation.id ?? index}>
            <summary>{label(explanation) || t("how_assessed")}</summary>
            <CopyBlock
              value={explanation.body ?? explanation.sections ?? explanation}
            />
          </details>
        ));
      case "partner_summary":
        return (
          <>
            <CopyBlock
              value={
                report.partner_summary?.summary ??
                report.partner_summary?.explanation
              }
            />
            {domainList(list(report.partner_summary?.areas))}
          </>
        );
      default:
        return null;
    }
  };
  const person = report.person,
    assessment = report.assessment;
  return (
    <article
      ref={root}
      className="mobility-render-document"
      lang={locale}
      data-report-id={report.report_id}
      data-report-revision={report.revision}
    >
      <header className="mr-report-header">
        <div className="mr-brand">
          SeniorLife<span>.AI</span>
        </div>
        <h1>{text(report.presentation.title)}</h1>
        <div className="mr-person">
          <Media
            src={reportPhotoSource(person)}
            name=""
            className="mr-person-photo"
            placeholder={
              <svg
                width="42"
                height="42"
                viewBox="0 0 48 48"
                fill="currentColor"
                aria-hidden="true"
              >
                <circle cx="24" cy="16" r="8" />
                <path d="M8 42v-4a16 16 0 0 1 32 0v4Z" />
              </svg>
            }
          />
          <div>
            <h2 className="notranslate" translate="no">
              {text(person.display_name ?? person.name)}
            </h2>
            <div className="mr-person-ids">
              {person.external_id_label && (
                <span>{person.external_id_label}: </span>
              )}
              <span className="notranslate" translate="no">
                {text(person.external_id ?? person.test_person_id)}
              </span>
              {person.seniorlife_id && (
                <span className="notranslate" translate="no">
                  {" "}
                  · {text(person.seniorlife_id)}
                </span>
              )}
            </div>
            <p>
              {text(person.demographics_label ?? person.demographics_display)}
            </p>
            {person.age_label && <span>{person.age_label} </span>}
            {person.gender_label && <span>{person.gender_label}</span>}
            {person.height?.display && <span> · {person.height.display}</span>}
            {person.weight?.display && <span> · {person.weight.display}</span>}
          </div>
        </div>
        <dl className="mr-facts">
          {[
            [
              t("assessment_date"),
              assessment.date_label ?? assessment.record_date_label,
            ],
            [t("generated_date"), report.context.generated_at_label],
            [t("location"), person.location_label ?? person.location],
            [t("equipment"), person.equipment_label ?? person.equipment_used],
            [t("injury"), person.injury_label ?? person.past_injury],
            [
              t("assessment"),
              assessment.assessor_label ?? assessment.assessed_by,
            ],
          ]
            .filter(([, value]) => value != null && value !== "")
            .map(([name, value], index) => (
              <div key={index}>
                <dt>{name}</dt>
                <dd className="notranslate" translate="no">
                  {text(value)}
                </dd>
              </div>
            ))}
        </dl>
        {!assessment.contributing_tests_label &&
          list(assessment.contributing_tests).length > 0 && (
            <div>
              <strong>{t("tests_included")}</strong>
              <ul>
                {list(assessment.contributing_tests).map((test, index) => (
                  <li key={index}>
                    {text(test.test_label ?? test.test_type)} ·{" "}
                    {text(test.date_label ?? test.date)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        {assessment.contributing_tests_label && (
          <p>
            <strong>{t("tests_included")}: </strong>
            {assessment.contributing_tests_label}
          </p>
        )}
        {report.context.fallback_notice && (
          <p className="mr-fallback-note">{report.context.fallback_notice}</p>
        )}
        {report.presentation.status_heading && (
          <div className="mr-section-heading mr-header-status">
            <strong>{report.presentation.status_heading}</strong>
            <Status value={report.headline} />
            <Trend value={report.headline?.trend} />
          </div>
        )}
        {list(report.explanations).find(
          (entry) => entry.id === "how_defined",
        ) && (
          <button
            type="button"
            onClick={() =>
              setDialog({
                title:
                  report.presentation.labels?.how_assessed ?? t("how_assessed"),
                content: list(report.explanations).find(
                  (entry) => entry.id === "how_defined",
                ),
              })
            }
          >
            {report.presentation.labels?.how_assessed ?? t("how_assessed")}
          </button>
        )}
      </header>
      {report.freshness.outdated && (
        <aside className="mr-report-freshness">
          <Freshness value={report.freshness} />
          <CopyBlock value={report.freshness.notice ?? report.freshness.note} />
        </aside>
      )}
      {activity &&
        !readOnly &&
        onActivityChange &&
        allowed(report.capabilities.can_change_activity) && (
          <label className="mr-activity-picker">
            {t("activity")}
            <select
              value={
                report.context.activity_id ??
                activity.activity?.id ??
                activity.selected_activity?.id ??
                ""
              }
              disabled={busy}
              onChange={(event) => onActivityChange(event.target.value)}
            >
              {list(activity.available_activities).map((option) => (
                <option
                  key={option.id}
                  value={option.id}
                  disabled={option.available === false}
                >
                  {label(option)}
                </option>
              ))}
            </select>
          </label>
        )}
      {sections.map((entry, index) => {
        const section = typeof entry === "string" ? { type: entry } : entry;
        if (section.visible === false) return null;
        return (
          <section
            className={`mr-section mr-section-${section.type}`}
            key={section.id ?? `${section.type}-${index}`}
            data-report-section={section.type}
          >
            {section.title && <h2>{section.title}</h2>}
            {sectionContent(section.type, section)}
          </section>
        );
      })}
      {report.presentation.footer && (
        <footer className="mr-report-footer">
          <CopyBlock value={report.presentation.footer} />
        </footer>
      )}
      <section className="mr-print-metric-details" aria-label={t("details")}>
        {printableMetricIds(report).map((id) => (
          <article key={id}>
            <h2>{label(metrics[id])}</h2>
            <MetricInformation metric={metrics[id]} t={t} />
          </article>
        ))}
      </section>
      {dialog && (
        <ReportDialog
          title={dialog.title}
          onClose={closeDialog}
          closeLabel={t("close")}
        >
          {dialog.metric ? (
            <MetricInformation metric={dialog.metric} t={t} />
          ) : (
            <CopyBlock value={dialog.content} />
          )}
        </ReportDialog>
      )}
    </article>
  );
}
export default RenderReportDocument;
